import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BellRing, CheckCircle2, Clock3, Printer } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { LoadError } from '../../components/layout/LoadError';
import { useToast } from '../../components/ui/toast';
import { usePolicyAcks, useRemindPolicy } from '../../hooks';
import { ApiError } from '../../lib/api-client';

function thaiDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function AcksSkeleton() {
  return (
    <div className="space-y-4 animate-pulse" aria-label="กำลังโหลดการรับทราบ">
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((key) => <div key={key} className="card h-24 bg-gray-100" />)}
      </div>
      <div className="card h-64 bg-gray-100" />
    </div>
  );
}

export function HRPolicyAcks() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = usePolicyAcks(id);
  const remind = useRemindPolicy();
  const toast = useToast();
  const navigate = useNavigate();
  const [reminding, setReminding] = useState(false);

  async function sendReminder() {
    if (!id) return;
    setReminding(true);
    try {
      const result = await remind.mutateAsync(id);
      if (result.pending === 0) {
        toast.success('ทุกคนรับทราบแล้ว ไม่มีรายการต้องทวง');
      } else if (result.failed > 0 || result.unavailable > 0) {
        toast.info(`ส่งถึง ${result.sent} คน · ไม่มีบัญชี LINE ${result.unavailable} คน · ส่งไม่สำเร็จ ${result.failed} คน`);
      } else {
        toast.success(`ส่งข้อความทวงแล้ว ${result.sent} คน`);
      }
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'ส่งข้อความทวงไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setReminding(false);
    }
  }

  return (
    <AdminLayout title="รายงานการรับทราบ">
      <style>{`
        @media screen and (max-width: 640px) {
          .policy-report-table thead { display: none; }
          .policy-report-table, .policy-report-table tbody,
          .policy-report-table tr, .policy-report-table td { display: block; width: 100%; }
          .policy-report-table tr { padding: 8px 12px; border-top: 1px solid #e5e7eb; }
          .policy-report-table td {
            display: flex; justify-content: space-between; gap: 16px;
            padding: 5px 0 !important; text-align: right !important; border: 0 !important;
          }
          .policy-report-table td::before {
            content: attr(data-label); color: #6b7280; font-size: 12px;
            text-align: left; flex: 0 0 38%;
          }
        }
        @media print {
          @page { size: A4 portrait; margin: 14mm; }
          body { background: white !important; color: black !important; }
          aside, header, .print-hidden { display: none !important; }
          main, main > div { margin: 0 !important; padding: 0 !important; max-width: none !important; }
          .print-sheet { display: block !important; width: 100% !important; }
          .print-card { border: 1px solid #aaa !important; box-shadow: none !important; break-inside: avoid; }
          table { font-size: 10pt !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>
      <div className="space-y-4 max-w-6xl print-sheet">
        <div className="flex gap-2 flex-wrap print-hidden">
          <button className="btn-secondary min-h-11" onClick={() => navigate('/hr/policies')}>
            <ArrowLeft className="w-4 h-4" /> กลับไปรายการ
          </button>
          <button className="btn-secondary min-h-11" onClick={() => window.print()} disabled={isLoading || !data}>
            <Printer className="w-4 h-4" /> พิมพ์ A4
          </button>
          {data?.policy.ack_required && data.summary.pending > 0 && data.policy.status === 'published' && (
            <button className="btn-cta min-h-11" onClick={() => void sendReminder()} disabled={reminding}>
              <BellRing className="w-4 h-4" /> {reminding ? 'กำลังส่งข้อความ…' : `ทวง ${data.summary.pending} คน`}
            </button>
          )}
        </div>

        {isLoading ? (
          <AcksSkeleton />
        ) : error || !data ? (
          <LoadError error={error} onRetry={() => void refetch()} />
        ) : (
          <>
            <section className="card p-5 print-card">
              <h1 className="text-xl font-semibold">รายงานการรับทราบเอกสาร</h1>
              <p className="font-medium mt-2">{data.policy.title}</p>
              <p className="text-sm text-gray-600">
                เลขที่ {data.policy.doc_no} · ฉบับที่ {data.policy.version} · มีผล {thaiDateTime(`${data.policy.effective_from}T00:00:00+07:00`)}
                {' ถึง '}{data.policy.effective_to ? thaiDateTime(`${data.policy.effective_to}T23:59:59+07:00`) : 'ไม่กำหนด'}
              </p>
              <p className="text-xs text-gray-500 mt-1">พิมพ์เมื่อ {thaiDateTime(new Date().toISOString())}</p>
            </section>

            <section className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="card p-4 print-card">
                <p className="text-xs text-gray-500">อยู่ในขอบเขตและช่วงจ้างงาน</p>
                <p className="text-2xl font-semibold tabular-nums">{data.summary.total}</p>
              </div>
              <div className="card p-4 print-card">
                <p className="text-xs text-gray-500">รับทราบแล้ว</p>
                <p className="text-2xl font-semibold text-green-700 tabular-nums">{data.summary.acknowledged}</p>
              </div>
              <div className="card p-4 print-card">
                <p className="text-xs text-gray-500">ยังไม่รับทราบ</p>
                <p className="text-2xl font-semibold text-amber-700 tabular-nums">{data.summary.pending}</p>
              </div>
            </section>

            <section className="card overflow-hidden print-card">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 font-semibold">
                <Clock3 className="w-5 h-5 text-amber-600 print-hidden" /> ยังไม่รับทราบ ({data.pending.length})
              </div>
              {data.pending.length === 0 ? (
                <p className="p-4 text-sm text-green-700">ไม่มีรายการค้างรับทราบ</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="policy-report-table w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">รหัส</th>
                        <th className="px-4 py-2 text-left">ชื่อพนักงาน</th>
                        <th className="px-4 py-2 text-left">แผนก</th>
                        <th className="px-4 py-2 text-left">ตำแหน่ง</th>
                        <th className="px-4 py-2 text-left">สถานะ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pending.map((person) => (
                        <tr key={person.emp_code} className="border-t border-gray-100">
                          <td data-label="รหัส" className="px-4 py-2 tabular-nums">{person.emp_code}</td>
                          <td data-label="ชื่อพนักงาน" className="px-4 py-2">{person.name}</td>
                          <td data-label="แผนก" className="px-4 py-2">{person.department || '—'}</td>
                          <td data-label="ตำแหน่ง" className="px-4 py-2">{person.position || '—'}</td>
                          <td data-label="สถานะ" className="px-4 py-2 text-amber-700">ยังไม่รับทราบ</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="card overflow-hidden print-card">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 font-semibold">
                <CheckCircle2 className="w-5 h-5 text-green-600 print-hidden" /> รับทราบแล้ว ({data.acknowledged.length})
              </div>
              {data.acknowledged.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">ยังไม่มีผู้รับทราบ</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="policy-report-table w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">รหัส</th>
                        <th className="px-4 py-2 text-left">ชื่อพนักงาน</th>
                        <th className="px-4 py-2 text-left">แผนก</th>
                        <th className="px-4 py-2 text-left">ตำแหน่ง ณ เวลารับทราบ</th>
                        <th className="px-4 py-2 text-left">รับทราบเมื่อ</th>
                        <th className="px-4 py-2 text-right">เวลาอ่าน</th>
                        <th className="px-4 py-2 text-center">ลายเซ็น</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.acknowledged.map((person) => (
                        <tr key={person.emp_code} className="border-t border-gray-100">
                          <td data-label="รหัส" className="px-4 py-2 tabular-nums">{person.emp_code}</td>
                          <td data-label="ชื่อพนักงาน" className="px-4 py-2">
                            {person.name}
                          </td>
                          <td data-label="แผนก" className="px-4 py-2">{person.department || '—'}</td>
                          <td data-label="ตำแหน่ง ณ เวลารับทราบ" className="px-4 py-2">{person.position || '—'}</td>
                          <td data-label="รับทราบเมื่อ" className="px-4 py-2 whitespace-nowrap">{thaiDateTime(person.acked_at)}</td>
                          <td data-label="เวลาอ่าน" className="px-4 py-2 text-right tabular-nums">{person.read_seconds ?? 0} วินาที</td>
                          <td data-label="ลายเซ็น" className="px-4 py-2 text-center">{person.signed ? 'มี' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
